import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ShineButton } from '../vip/VipPlanMotion';

/**
 * 인스타그램 계정 연결 여정 공용 디자인 키트 (2026-11-03 대표 "UI UX를 최고급으로 만드는 노하우가 이미 축적이 되어 있는데
 * 그 UI가 디자인이 너무 구식인 것 같아").
 *
 * 기준(우리 노하우 그대로): 지식도서관 knowledge/web-design/premium-web-3d-motion-guide-2026-10-14.md(대표 '와 대박이네' 블랭킷 결제 5단계 —
 *  번호 단계 띠·왼쪽 정렬 제목·실물 미리보기 시트·먹색(여기선 흰) 캡슐 54px·곡선 2개·앞뒤 방향 전환·피할 'AI 랜딩 모양'),
 *  디자인 절대기준 v2(Dark Editorial), 구성 먼저(화면마다 주인공 하나·상자 줄이기),
 * 대표가 칭찬한 VIP 페이지 레시피(흰 캡슐 1개·강조 2곳·한 스크롤·움직임은 의미 있을 때만), 가 계정 키트(altAccountUi).
 *  - 카드 안에 카드를 넣지 않는다. 구획은 헤어라인.
 *  - 주 버튼은 흰 캡슐 하나(브랜드색 그라데이션 버튼·→ 화살표 금지). 보조는 외곽선 캡슐이나 글자 단추.
 *  - 강조색(SDL 파랑)은 화면당 한두 곳만.
 *  - 문구는 예전 화면 문장을 그대로 쓴다 — 디자인만 바꾼다.
 */

export const CONNECT_ACCENT = '#3182f6';
/** 곡선은 두 개만(10/14 안내서 4-1): 빨리 출발해 부드럽게 멈춤 / 살짝 넘었다 돌아옴 */
export const EASE_OUT = [0.2, 0.8, 0.2, 1] as const;
export const EASE_SPRING = [0.3, 1.35, 0.5, 1] as const;
export type ConnectDir = 'forward' | 'back';

/** Instagram 로고(iconsax 인스타 아이콘 렌더 버그로 직접 그림) */
export const InstagramGlyph: React.FC<{ size?: number; color?: string; className?: string }> = ({ size = 20, color = '#FAFAFA', className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
        <rect x="3" y="3" width="18" height="18" rx="5.5" stroke={color} strokeWidth="1.8" />
        <circle cx="12" cy="12" r="4.1" stroke={color} strokeWidth="1.8" />
        <circle cx="17.3" cy="6.7" r="1.15" fill={color} />
    </svg>
);

export type ConnectStepId = 'intro' | 'input' | 'confirm' | 'connecting' | 'success';

const STEPS: { id: ConnectStepId; label: string }[] = [
    { id: 'intro', label: '시작' },
    { id: 'input', label: '계정' },
    { id: 'confirm', label: '확인' },
    { id: 'connecting', label: '연결' },
    { id: 'success', label: '완료' },
];

/** 단계 띠 — 번호 원 다섯 개 + 이름, 아래 가는 선이 지금 단계까지 채워진다(10/14 안내서의 단계 띠를 어두운 화면으로) */
export const ConnectStepBar: React.FC<{ current: ConnectStepId }> = ({ current }) => {
    const reduce = useReducedMotion();
    const idx = STEPS.findIndex((s) => s.id === current);
    return (
        <div className="w-full" aria-label={`연결 단계 ${idx + 1}/${STEPS.length}`}>
            <div className="flex justify-between">
                {STEPS.map((s, i) => {
                    const done = i < idx;
                    const now = i === idx;
                    return (
                        <div key={s.id} className="flex w-12 flex-col items-center gap-1.5">
                            <motion.span
                                initial={false}
                                animate={{ scale: now && !reduce ? 1.08 : 1 }}
                                transition={{ duration: 0.4, ease: EASE_SPRING }}
                                className={`flex h-[22px] w-[22px] items-center justify-center rounded-full text-[11px] font-semibold ${
                                    now ? 'bg-white text-[#0A0A0A]' : done ? 'border border-white/50 text-[#D4D4D8]' : 'border border-white/[0.16] text-[#52525B]'
                                }`}
                                style={{ fontVariantNumeric: 'tabular-nums' }}
                            >
                                {i + 1}
                            </motion.span>
                            <span className={`text-[11px] ${now ? 'font-semibold text-[#FAFAFA]' : done ? 'text-[#A1A1AA]' : 'text-[#52525B]'}`}>{s.label}</span>
                        </div>
                    );
                })}
            </div>
            <div className="mt-3 h-px w-full bg-white/[0.08]">
                <motion.div
                    className="h-px origin-left bg-white/70"
                    initial={false}
                    animate={{ scaleX: (idx + 0.5) / STEPS.length }}
                    transition={{ duration: reduce ? 0 : 0.7, ease: EASE_OUT }}
                />
            </div>
        </div>
    );
};

/** 화면 틀 — 검은 바탕 가운데 한 줄(휴대폰·PC 같음). 앞으로 가면 오른쪽에서, 뒤로 가면 왼쪽에서 들어온다(10/14 안내서 4-3).
 *  아래 버튼 자리는 화면 바닥에 고정, 위에 가는 선 하나(흐림 효과 없음).
 *  sticky가 아니라 fixed인 까닭: 사이트 틀(Layout)의 본문이 overflow-x-hidden이라 sticky가 붙지 않는다 —
 *  2026-11-03 실측(머리띠·안내 띠까지 넣으니 첫 화면에서 '연결 시작하기'가 화면 밖 935px). VIP 페이지와 같은 방식.
 *  버튼 자리 높이만큼 제자리(빈칸)를 재서 두고, 손님이 끝까지 내려 그 제자리가 다 보이면 버튼이 제자리에 내려앉는다
 *  (sticky와 같은 움직임) — 그래야 사이트 꼬리의 사업자 정보를 버튼이 가리지 않는다. */
export const ConnectShell: React.FC<{ step: ConnectStepId; dir?: ConnectDir; children: React.ReactNode; footer?: React.ReactNode }> = ({ step, dir = 'forward', children, footer }) => {
    const reduce = useReducedMotion();
    const slotRef = React.useRef<HTMLDivElement>(null);
    const footRef = React.useRef<HTMLDivElement>(null);
    const [footH, setFootH] = React.useState(0);
    const [docked, setDocked] = React.useState(false);
    // 아이폰 사파리는 자판이 올라와도 화면 틀이 줄지 않아 바닥 고정 띠가 자판 뒤로 숨는다 → 보이는 화면(visualViewport) 높이로 자판 높이를 재 띠를 그만큼 올린다(독립 검토 11/3)
    const [kb, setKb] = React.useState(0);
    React.useEffect(() => {
        const vv = typeof window !== 'undefined' ? window.visualViewport : null;
        if (!vv) return;
        const update = () => {
            const inset = Math.round(window.innerHeight - vv.height - vv.offsetTop);
            setKb(inset > 80 ? inset : 0);
        };
        update();
        vv.addEventListener('resize', update);
        vv.addEventListener('scroll', update);
        return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
    }, []);
    React.useLayoutEffect(() => {
        const el = footRef.current;
        if (!el) return;
        const update = () => setFootH(Math.ceil(el.getBoundingClientRect().height));
        update();
        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [footer]);
    React.useEffect(() => {
        const slot = slotRef.current;
        if (!slot || typeof IntersectionObserver === 'undefined') return;
        const io = new IntersectionObserver(([e]) => setDocked(e.intersectionRatio >= 0.99), { threshold: [0, 0.99, 1] });
        io.observe(slot);
        return () => io.disconnect();
    }, [footer, footH]);
    return (
        <div className="flex w-full flex-col bg-black text-[#FAFAFA]" style={{ minHeight: '100dvh' }}>
            <div className="mx-auto w-full max-w-[448px] flex-1 px-5 pt-6 md:pt-10">
                <ConnectStepBar current={step} />
                <motion.div
                    key={step}
                    initial={reduce ? false : { opacity: 0, x: dir === 'back' ? -36 : 36 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.42, ease: EASE_OUT }}
                    className="pt-9 pb-8"
                >
                    {children}
                </motion.div>
            </div>
            {footer && (
                <div ref={slotRef} style={{ height: footH || undefined }}>
                    <div
                        ref={footRef}
                        className={`${docked && !kb ? 'relative' : 'fixed inset-x-0 bottom-0'} z-40 border-t border-white/[0.08] bg-black`}
                        style={!docked || kb ? { bottom: kb } : undefined}
                    >
                        <div className="mx-auto w-full max-w-[448px] px-5 pt-4" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
                            {footer}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/** 왼쪽 정렬 제목 + 설명(10/14 안내서 2-5 제목 값) */
export const ConnectTitle: React.FC<{ title: React.ReactNode; sub?: React.ReactNode }> = ({ title, sub }) => (
    <div>
        <h1 className="text-[28px] font-bold text-[#FAFAFA]" style={{ lineHeight: 1.22, letterSpacing: '-0.025em', textWrap: 'balance' as never }}>{title}</h1>
        {sub && <p className="mt-2.5 text-[15px] leading-relaxed text-[#A1A1AA] break-keep">{sub}</p>}
    </div>
);

/** 실물 미리보기 시트 — 한 단계 올라온 면(모서리 28) */
export const ConnectSheet: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
    <div className={`rounded-[28px] border border-white/[0.08] bg-[#0F0F11] ${className}`}>{children}</div>
);

/** 주 버튼 — 흰 캡슐(숨 쉬는 광채, 누르면 살짝 들어감). 화면에 하나만. */
export const ConnectPrimaryButton: React.FC<{ children: React.ReactNode; onClick?: () => void; disabled?: boolean; type?: 'button' | 'submit'; loading?: boolean }> = ({ children, onClick, disabled, type, loading }) => (
    // loading: 기다리는 중에도 흰 캡슐 그대로 보여 '돌아가는 중'이 보이게(누름은 막음) — 2026-11-03 대표 "로딩 … 안 나오는데"
    <ShineButton
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        className={`w-full h-[54px] rounded-full text-[16px] font-semibold transition-colors ${
            loading ? 'bg-white text-[#0A0A0A] cursor-wait' : disabled ? 'bg-white/[0.07] text-[#52525B] cursor-not-allowed' : 'bg-white text-[#0A0A0A] hover:bg-white/90'
        }`}
    >
        {children}
    </ShineButton>
);

/** 보조 버튼 — 외곽선 캡슐 */
export const ConnectSecondaryButton: React.FC<{ children: React.ReactNode; onClick?: () => void; disabled?: boolean }> = ({ children, onClick, disabled }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="w-full h-12 rounded-full border border-white/[0.14] text-[14px] font-medium text-[#D4D4D8] hover:bg-white/[0.04] active:scale-[0.99] transition disabled:opacity-40"
    >
        {children}
    </button>
);

/** 글자 단추 — 가장 낮은 단계의 행동(이전으로 등) */
export const ConnectTextButton: React.FC<{ children: React.ReactNode; onClick?: () => void; disabled?: boolean }> = ({ children, onClick, disabled }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="w-full py-3 text-[14px] text-[#A1A1AA] hover:text-[#FAFAFA] transition-colors disabled:opacity-40"
    >
        {children}
    </button>
);

/** 헤어라인 목록 한 줄 — 아이콘 상자 없이 작은 아이콘 + 제목/설명 */
export const ConnectRow: React.FC<{ icon?: React.ReactNode; title: React.ReactNode; description?: React.ReactNode }> = ({ icon, title, description }) => (
    <div className="flex items-start gap-3.5 py-4 border-t border-white/[0.07] first:border-t-0">
        {icon && <div className="mt-0.5 shrink-0 text-[#A1A1AA]">{icon}</div>}
        <div className="min-w-0">
            <p className="text-[15px] font-semibold text-[#FAFAFA] leading-snug">{title}</p>
            {description && <p className="mt-0.5 text-[13px] text-[#71717A] leading-relaxed">{description}</p>}
        </div>
    </div>
);

/** 각주 — 규칙·안심 문구는 상자 대신 작은 글씨 */
export const ConnectFootnote: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
    <p className={`text-[12px] leading-relaxed text-[#8B8B94] text-center ${className}`}>{children}</p>
);

/** 오류 한 줄 — 빨간 상자 대신 빨간 점 + 글씨 */
export const ConnectError: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex items-start gap-2 text-[13px] leading-relaxed text-[#F87171]" role="alert">
        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#F87171]" />
        <span>{children}</span>
    </div>
);
